import React, { useMemo, useState } from 'react';
import {
  CHANNEL_DEFS,
  DEFAULT_ACTIVE_CHANNELS,
  DEFAULT_CHANNEL_DATA,
  HOW_STEPS,
  calculateChannelComparison,
  formatCount,
  formatMoney,
  getChannelRecommendations,
} from './channelCompareLogic';
import RevenueLeversNumberField from '../RevenueLevers/RevenueLeversNumberField';
import '../RevenueLevers/RevenueLevers.css';
import './ChannelCompare.css';

const ChannelCompare = () => {
  const locale = 'ru-RU';
  const [active, setActive] = useState(DEFAULT_ACTIVE_CHANNELS);
  const [data, setData] = useState(DEFAULT_CHANNEL_DATA);

  const toggleChannel = (id) => {
    setActive((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const setChannelField = (id, field) => (value) => {
    setData((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const comparison = useMemo(
    () => calculateChannelComparison(active, data),
    [active, data],
  );

  const recommendations = useMemo(
    () => getChannelRecommendations(comparison),
    [comparison],
  );

  const hasSelection = active.length > 0;

  return (
    <div className="cc-tool">
      <div className="cc-pick">
        <div className="rl-panel-title">{'Выберите каналы'}</div>
        <p className="cc-pick-hint">{'Выберите источники. Невыбранные не участвуют в расчёте.'}</p>
        <div className="cc-chips">
          {CHANNEL_DEFS.map((ch) => {
            const isOn = active.includes(ch.id);
            return (
              <button
                key={ch.id}
                type="button"
                className={`cc-chip${isOn ? ' cc-chip--on' : ''}`}
                onClick={() => toggleChannel(ch.id)}
              >
                {ch.name}
              </button>
            );
          })}
        </div>
      </div>

      {!hasSelection ? (
        <div className="cc-empty">{'Выберите хотя бы один канал, чтобы увидеть сравнение.'}</div>
      ) : (
        <>
          <div className="cc-forms">
            {CHANNEL_DEFS.filter((ch) => active.includes(ch.id)).map((ch) => (
              <div key={ch.id} className="rl-panel cc-form-panel">
                <div className="rl-panel-title">{ch.name}</div>
                <div className="cc-form-grid">
                  <RevenueLeversNumberField
                    label={'Расход на рекламу'}
                    unit={'₸'}
                    value={data[ch.id]?.spend ?? 0}
                    onChange={setChannelField(ch.id, 'spend')}
                    min={0}
                  />
                  <RevenueLeversNumberField
                    label={'Лидов в месяц'}
                    unit={'шт'}
                    value={data[ch.id]?.leads ?? 0}
                    onChange={setChannelField(ch.id, 'leads')}
                    min={0}
                  />
                  <RevenueLeversNumberField
                    label={'Выручка с рекламных лидов'}
                    unit={'₸'}
                    value={data[ch.id]?.revenue ?? 0}
                    onChange={setChannelField(ch.id, 'revenue')}
                    min={0}
                  />
                </div>
              </div>
            ))}
          </div>

          {comparison.rows.length > 0 && (
            <>
              <div className="rl-section-title">{'Сценарии масштабирования'}</div>
              <div className="rl-table-wrap">
                <table className="rl-table">
                  <thead>
                    <tr>
                      <th>{'Канал'}</th>
                      <th>{'Расход на рекламу'}</th>
                      <th>{'Лиды'}</th>
                      <th>{'CPL'}</th>
                      <th>{'Выручка'}</th>
                      <th>{'ROMI'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparison.rows.map((row) => {
                      const ch = CHANNEL_DEFS.find((c) => c.id === row.id);
                      const isBestCpl = comparison.bestCpl?.id === row.id && comparison.rows.length > 1;
                      const isBestRomi = comparison.bestRomi?.id === row.id && comparison.rows.length > 1;
                      return (
                        <tr key={row.id}>
                          <td>
                            {ch ? ch.name : row.id}
                            {isBestCpl && <span className="cc-badge cc-badge--cpl">{'Лучший CPL'}</span>}
                            {isBestRomi && <span className="cc-badge cc-badge--romi">{'Лучший ROMI'}</span>}
                          </td>
                          <td>{formatMoney(row.spend, locale)}</td>
                          <td>{formatCount(row.leads, locale)}</td>
                          <td>{row.cpl != null ? formatMoney(row.cpl, locale) : '—'}</td>
                          <td>{formatMoney(row.revenue, locale)}</td>
                          <td className={row.romi != null && row.romi >= 0 ? 'cc-romi--pos' : 'cc-romi--neg'}>
                            {row.romi != null ? `${Math.round(row.romi)}%` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="cc-rec">
                <div className="rl-panel-title">{'Выводы и рекомендации'}</div>
                <ul className="cc-rec-list">
                  {recommendations.map((rec, i) => (
                    <li key={`${rec.text}-${i}`}>{rec.text}</li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </>
      )}

      <div className="rl-how">
        <div className="rl-panel-title">{'Как считается'}</div>
        {HOW_STEPS.map((step) => (
          <div key={step.num} className="rl-how-step">
            <div className="rl-how-num">{step.num}</div>
            <div className="rl-how-text">
              <strong>{step.title}</strong> {step.text}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChannelCompare;
